/**
 * Global conversation context (α3/α4): one outer lifecycle mount for the v2 app;
 * conversation transport resets when lifecycle id or resolved project boundary changes
 * (`useConversation` surface key) without keyed remounts.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'wouter';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

import {
  useConversation,
  type ConversationReturn,
} from '@/hooks/v2/useConversation';
import { useScopingSnapshot } from '@/hooks/v2/useScopingSnapshot';
import {
  useConversationLifecycle,
  type UseConversationLifecycleReturn,
} from '@/hooks/v2/useConversationLifecycle';
import { deriveModeFromPathname } from '@/hooks/v2/conversationLifecycle';
import { resolveSurfaceMode, shouldResumeConversation } from './resolveSurfaceMode';
import { handoffMatchesSurface, type ContinuationHandoff } from '@/lib/continuationHandoff';
import { useConversationSurfaceShortId } from '@/hooks/v2/useConversationSurfaceShortId';
import { useProjectByShortId } from '@/hooks/v2/useProjectByShortId';
import { useProjects, type ProjectListItem } from '@/hooks/v2/useProjects';
import { useProfileSettings } from '@/hooks/v2/useProfileSettings';
import { PersistentVoiceAudio } from '@/components/v2/conversation/PersistentVoiceAudio';

// Exported for Storybook mock providers only — do not import in production code.
export const ConversationContext = createContext<ConversationReturn | null>(null);

export const ConversationLifecycleContext =
  createContext<UseConversationLifecycleReturn | null>(null);

/**
 * 020 — request a warm cross-page continuation (D2). The single-use handoff is
 * held in the singleton provider so it survives the navigation/layout
 * transition that drops a per-component ref. The LLM `navigate` action handler
 * (US3) calls `requestContinuation`; the destination consumes it (US2).
 */
export interface ConversationNavigation {
  requestContinuation: (handoff: ContinuationHandoff) => void;
}
export const ConversationNavigationContext = createContext<ConversationNavigation | null>(null);

export function useConversationNavigation(): ConversationNavigation {
  const ctx = useContext(ConversationNavigationContext);
  if (!ctx) {
    throw new Error('useConversationNavigation must be used within <ConversationProvider>');
  }
  return ctx;
}

interface ProviderProps {
  userId: string;
  /**
   * Active organisation id (from UserOrgsContext). Threaded into the lifecycle
   * so a deliberate org switch tears down the conversation — preventing a stale
   * id from one org being reused in another (cross-tenant isolation). Optional
   * so isolated mounts / Storybook can omit it.
   */
  orgId?: string | null;
  children: ReactNode;
}

export function useConversationContext(): ConversationReturn {
  const ctx = useContext(ConversationContext);
  if (!ctx) {
    throw new Error(
      'useConversationContext must be used within <ConversationProvider>',
    );
  }
  return ctx;
}

export function useConversationLifecycleContext(): UseConversationLifecycleReturn {
  const ctx = useContext(ConversationLifecycleContext);
  if (!ctx) {
    throw new Error(
      'useConversationLifecycleContext must be used within <ConversationProvider>',
    );
  }
  return ctx;
}

/** Maps a gateway `navigate` payload to a client-side route path. */
function resolveNavigateRoute(event: {
  target: string;
  project_short_id?: string;
  tab?: string;
}): string | null {
  if (event.target === 'home') return '/';
  if (event.target === 'project') {
    if (!event.project_short_id) return null;
    const tab = event.tab ?? 'overview';
    return `/projects/${event.project_short_id}/${tab}`;
  }
  return null;
}

function ConversationInner({ orgId, children }: { orgId?: string | null; children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [pathname, setLocation] = useLocation();
  const surfaceShortId = useConversationSurfaceShortId();
  const projectQuery = useProjectByShortId(surfaceShortId);
  const lifecycle = useConversationLifecycleContext();
  const allProjects = useProjects();

  // When there is exactly one active project and the lifecycle is in home mode,
  // treat the conversation as update mode for that project. This avoids a
  // pointless general conversation when the user's workspace has a clear focus.
  // Lazy-start is preserved — proactiveStart stays false on the home route.
  //
  // IMPORTANT: the decision is frozen for the duration of the active lifecycle
  // (lifecycle.conversationId !== null). useProjects() is a react-query result
  // that may resolve AFTER the user has already typed; without freezing, the
  // single-project shortcut flips effectiveMode home→update, the surfaceKey
  // recomputes, useConversation's reset effect fires, and the optimistic user
  // turn is wiped from `turns`. Once lifecycle.conversationId is reset to null
  // (explicit lifecycle.reset() or idle expiry), the lock is released.
  //
  // We cache the full project (not just the id) so that a subsequent refetch
  // dropping the project from the list — soft delete, archive, permission
  // change — cannot make singleProject collapse back to null mid-conversation
  // and flip effectiveMode 'update' → 'home' / projectId → ''.
  const candidateSingleProject =
    lifecycle.mode === 'home' && allProjects.data?.length === 1
      ? allProjects.data[0]
      : null;

  const frozenSingleProjectRef = useRef<ProjectListItem | null>(candidateSingleProject);
  if (lifecycle.conversationId === null) {
    frozenSingleProjectRef.current = candidateSingleProject;
  }
  const singleProject = lifecycle.conversationId === null
    ? candidateSingleProject
    : frozenSingleProjectRef.current;

  // 017 — derive the scoping mode SYNCHRONOUSLY from the route so a lagging
  // lifecycle.mode (it catches up to the route one effect tick later) can't
  // start the scoping call in the previous surface's mode (e.g. 'update' from
  // /overview, or a recovered session). Off /scoping the prior precedence
  // (single-project shortcut → lifecycle mode) is unchanged.
  const routeMode = deriveModeFromPathname(pathname);
  const effectiveMode = resolveSurfaceMode(routeMode, Boolean(singleProject), lifecycle.mode);
  const projectId = singleProject?.id ?? projectQuery.data?.id ?? '';

  // surfaceShortId is derived from useActiveProjectResolver on the home route
  // and returns the single project's short_id when there is exactly one. If
  // the project list later empties (refetch, archive, permission), the
  // resolver drops to null and surfaceKey would otherwise change, wiping the
  // in-flight transcript. Freeze the surface shortId alongside the project
  // decision so both inputs to surfaceKey remain stable while a lifecycle
  // conversation is active.
  const frozenSurfaceShortIdRef = useRef<string | null>(surfaceShortId);
  if (lifecycle.conversationId === null) {
    frozenSurfaceShortIdRef.current = surfaceShortId;
  }
  const effectiveSurfaceShortId = lifecycle.conversationId === null
    ? surfaceShortId
    : frozenSurfaceShortIdRef.current;

  // Track lifecycle.conversationId to detect rotations (UUID-A → UUID-B via
  // explicit reset) vs first mints (null → UUID on first message sent). The
  // surfaceResetCount increments ONLY on rotations so that the first message
  // on the home route does not clear the in-progress transcript — the home
  // transcript must survive the lifecycle id first being minted.
  const prevLifecycleIdRef = useRef<string | null>(lifecycle.conversationId);
  const [surfaceResetCount, setSurfaceResetCount] = useState(0);

  useEffect(() => {
    const prev = prevLifecycleIdRef.current;
    prevLifecycleIdRef.current = lifecycle.conversationId;
    // Only count rotations (non-null → different non-null); skip first mint.
    if (
      prev !== null &&
      lifecycle.conversationId !== null &&
      prev !== lifecycle.conversationId
    ) {
      setSurfaceResetCount((c) => c + 1);
    }
  }, [lifecycle.conversationId]);

  // Lazy-start UX: chat on home AND update should be user-initiated — no
  // auto-`session.start` until the user actually types. The one exception is
  // when a live conversation is already in flight (e.g. user typed on home
  // and then navigated into a project, or refreshed mid-conversation):
  // `lifecycle.conversationId` is non-null in those cases, and we want the
  // existing session to resume rather than asking the user to retype.
  // Non-home project routes still require a resolved `projectId` so the
  // payload carries the correct project context.
  // Drive proactive (auto-)resume off the SYNCHRONOUS route mode — lifecycle.mode
  // lags by an effect tick, which would otherwise resume a *locked* scope under
  // the previous mode before the guard applies, firing a turn at n8n (FR-005).
  // 020 — single-use, target-bound continuation handoff (D2). Held here in the
  // singleton provider so it survives navigation/layout transitions. Enqueued by
  // the LLM `navigate` action handler (US3) via `requestContinuation`; the
  // destination consume/clear timing is US2. With no handoff (the only state in
  // production today) `continuationRequested` is false → every plain navigation
  // stays lazy, identical to #627.
  const [pendingHandoff, setPendingHandoff] = useState<ContinuationHandoff | null>(null);
  const requestContinuation = useCallback((handoff: ContinuationHandoff) => {
    setPendingHandoff(handoff);
  }, []);
  const continuationRequested = handoffMatchesSurface(pendingHandoff, {
    surface: pathname,
    projectShortId: surfaceShortId ?? undefined,
  });

  // T013 / ANA-159 — single-use handoff consume + clear (020 US2 / D2). Runs
  // after the render where `continuationRequested` first becomes true, so
  // `proactiveStart` has already been computed and passed down before we clear.
  // Clearing here gives single-use semantics: the handoff fires exactly one
  // proactive continuation, then can never re-trigger after a lifecycle reset
  // (idle expiry, explicit reset) returns the user to the same surface.
  //
  // We deliberately do NOT discard on a non-matching surface: the handoff must
  // survive the in-flight navigation transition where `pathname` can briefly lag
  // the enqueued target (this is the whole reason the handoff lives in the
  // singleton provider rather than a per-component ref — D2). `requestContinuation`
  // is always paired with a `setLocation`/`navigate` to the same target, so a
  // mismatch is only ever the transient lag — never a permanent wrong-destination.
  useEffect(() => {
    if (!continuationRequested) return;
    // T015 path: a user-text-first handoff (navigate_and_send) sends the queued
    // message instead of a proactive Anaka-first greeting.
    if (pendingHandoff?.pendingUserMessage && sendMessageRef.current) {
      void sendMessageRef.current(pendingHandoff.pendingUserMessage);
    }
    setPendingHandoff(null);
  }, [continuationRequested, pendingHandoff]);

  // 017/020 — the global provider is LAZY: plain navigation never auto-fires a
  // turn (the regression where opening a project page sent a message). Scoping
  // greets at the page level (ChatThread proactiveStart). The ONLY global
  // proactive trigger is an explicit cross-page continuation that targets THIS
  // surface (rule 4).
  const proactiveStart = shouldResumeConversation({
    mode: routeMode,
    hasProjectId: Boolean(projectQuery.data?.id),
    hasConversationId: Boolean(lifecycle.conversationId),
    projectStatus: projectQuery.data?.status,
    continuationRequested,
  });
  // T020 — handle LLM `navigate` SSE: route client + enqueue warm continuation.
  const handleNavigate = useCallback((event: {
    target: string;
    project_short_id?: string;
    tab?: string;
    conversation_id?: string;
  }) => {
    const targetRoute = resolveNavigateRoute(event);
    if (!targetRoute) return;
    const convId = event.conversation_id ?? lifecycle.conversationId ?? '';
    if (convId) {
      requestContinuation({
        targetSurface: targetRoute,
        targetProjectShortId: event.project_short_id,
        conversationId: convId,
      });
    }
    setLocation(targetRoute);
  }, [requestContinuation, lifecycle.conversationId, setLocation]);

  const navigation = useMemo<ConversationNavigation>(() => ({ requestContinuation }), [requestContinuation]);
  const { data: profileSettings } = useProfileSettings();

  // Scoping resume detection: when on the scoping route, check briefing-context
  // for an existing session to reconnect to. Shares the React Query cache with
  // ProjectScopingPage's useScopingSnapshot call (same key, no extra request).
  // Only active for scoping; null projectId disables the query on other routes.
  const isScoping = effectiveMode === 'scoping';
  const scopingResume = useScopingSnapshot({
    projectId: isScoping && projectId ? projectId : null,
    refreshKey: 0,
  });
  // resumeConversationId carries DB-ground-truth semantics from briefing-context:
  //   string  — resumable session found; takes precedence over stale sessionStorage
  //   null    — briefing-context loaded with no resumable session; override stale sessionStorage
  //   undefined — still loading, errored, or non-scoping route; keep existing behaviour
  // Error is treated as undefined (not null) so a transient network failure never
  // forces a cold start by overriding a valid sessionStorage ID.
  // The !lifecycle.conversationId guard is intentionally absent: briefing-context
  // is authoritative for scoping and must override a stale sessionStorage ID.
  // A resumable scoping session is either healthy (auto-resume) or expired
  // (resume only on explicit re-entry). Both expose a conversation id to
  // reconnect to — its transcript + snapshot are durable in Postgres regardless
  // of TTL; expiry just DEFERS transcript hydration to the orient-card click.
  const hasResumableScopingSession = scopingResume.isResumable || scopingResume.isExpired;
  const resumeConversationId: string | null | undefined =
    !isScoping || scopingResume.isLoading || scopingResume.isError
      ? undefined
      : hasResumableScopingSession
        ? (scopingResume.conversationId ?? null)
        : null;
  // Expired resume: hold transcript hydration until the user re-enters (voice/text
  // click → startSession). useConversation gates the hydrate effect on this.
  const resumeIsDeferred = isScoping && scopingResume.isExpired;

  // ANA-309 (FR-002d): briefing-context is scoping's authoritative resume source.
  // Until it resolves, the stored sessionStorage id is NOT yet confirmed
  // resumable, so silent transcript hydration must wait — otherwise the hydrate
  // effect reads `GET /:id/turns` for a stale/deleted id and surfaces the
  // dmo0002 "turn errors". Once resolved, hydration runs against the authoritative
  // `resumeConversationId` (string → hydrate that id; null → no hydrate, fresh).
  const briefingResolved = isScoping && !scopingResume.isLoading && !scopingResume.isError;
  const detailedSurfaceReady = isScoping ? briefingResolved : false;

  // ANA-309 (FR-002d): when briefing-context resolves with nothing resumable but
  // the current id is the one we recovered from sessionStorage (an unconfirmed,
  // now-dead id), clear it so the next turn mints a fresh conversation — the
  // client must never stay pointed at a phantom/dead conversation. We gate on
  // the *recovered* id (not just any id) so the freshly-minted replacement from
  // `reset()` — and any user-minted id — is never reset again (no loop). This
  // replaces the prior reliance on a hydrate 404 to trigger the reset.
  const lifecycleReset = lifecycle.reset;
  useEffect(() => {
    if (!briefingResolved) return;
    // An expired session is still resumable (on explicit re-entry), so it must
    // NOT be reset away here — only a truly dead id (not found / no resume
    // memory) gets cleared so the next turn mints fresh.
    if (hasResumableScopingSession) return;
    const cid = lifecycle.conversationId;
    if (!cid) return;
    if (cid !== lifecycle.recoveredId) return; // only the untouched recovered (stale) id
    lifecycleReset();
  }, [
    briefingResolved,
    hasResumableScopingSession,
    lifecycle.conversationId,
    lifecycle.recoveredId,
    lifecycleReset,
  ]);

  // T052 (FR-002b): find-on-mount for non-scoping surfaces. A fresh client
  // (cleared storage / new device) has no stored lifecycle id; ask the server
  // for its single resumable conversation for (mode, project) and adopt it, so
  // the next user message continues that conversation instead of minting a new
  // one. Lazy-start is preserved — adopting an id never fires `/start`. Scoping
  // resumes via briefing-context (above), so it's excluded; interview has no
  // route surface here. `adopt_found_conversation` no-ops if an id already
  // exists (recovered/active), so a benign race with sessionStorage recovery
  // cannot clobber a real id.
  const lifecycleDispatch = lifecycle.dispatch;
  const findOnMountRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated) return; // no session yet (e.g. /login) — nothing to find
    if (effectiveMode === 'scoping') return;
    if (lifecycle.conversationId !== null) return;
    if (effectiveMode === 'update' && !projectId) return; // wait for the project to resolve
    // Cross-org isolation: never find under the wrong org. A project-less (home)
    // find resolves org server-side from the caller's DEFAULT membership unless we
    // pass the active org, so without this a post-org-switch home landing could
    // re-adopt the previous org's conversation. Wait for the active org, pass it,
    // and re-key on it so an org switch re-evaluates for the new org.
    if (!orgId) return;
    const key = `${effectiveMode}:${projectId || ''}:${orgId}`;
    if (findOnMountRef.current === key) return; // already attempted for this surface+org
    findOnMountRef.current = key;

    let cancelled = false;
    const params = new URLSearchParams({ mode: effectiveMode });
    if (projectId) params.set('project_id', projectId);
    params.set('org_id', orgId);
    void (async () => {
      try {
        const res = await fetch(`/api/v1/v2/conversation/find?${params.toString()}`, {
          credentials: 'include',
        });
        if (cancelled || !res.ok) return;
        const json = (await res.json()) as { conversation_id?: string | null };
        const foundId = json?.conversation_id ?? null;
        if (cancelled || !foundId) return;
        lifecycleDispatch({ kind: 'adopt_found_conversation', conversationId: foundId, at: Date.now() });
      } catch {
        // Transient/network — fail open to a fresh conversation.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, effectiveMode, projectId, lifecycle.conversationId, lifecycleDispatch, orgId]);

  // T015 — stable ref to the wrapped sendMessage so the T013 consume/clear
  // effect can dispatch a user-text-first handoff message without closing over
  // a stale callback. Updated synchronously after each render.
  const sendMessageRef = useRef<ConversationReturn['sendMessage'] | null>(null);
  const conversation = useConversation({
    projectId,
    mode: effectiveMode,
    conversationId: lifecycle.conversationId,
    lifecycleSurfaceResetSignal: surfaceResetCount,
    conversationSurfaceShortId: effectiveSurfaceShortId ?? 'home',
    currentPage: pathname || undefined,
    proactiveStart,
    defaultInputMode: profileSettings?.inputMode ?? 'text',
    onNavigate: handleNavigate,
    onCloudIdleEnded: () => {
      lifecycle.dispatch({ kind: 'cloud_idle_ended' });
    },
    onCloudGuardrailEnded: () => {
      lifecycle.dispatch({ kind: 'cloud_guardrail_ended' });
    },
    // ANA-575 — the voice agent intentionally ended the session. A `guardrail`
    // end is a hard close (same terminal behaviour as the text guardrail path →
    // "Start again"/route home); everything else is a graceful
    // `cloud_session_ended` that flips `isEnded` so scoping shows the Scoping
    // Locked screen (scope_locked/complete) and other modes show the ended
    // surface. This is what makes the scoping call actually close down after the
    // spoken goodbye instead of leaving a dead voice bar over the live thread.
    onVoiceSessionEnded: (reason) => {
      if (reason === 'guardrail') {
        lifecycle.dispatch({ kind: 'cloud_guardrail_ended' });
      } else {
        lifecycle.dispatch({ kind: 'cloud_session_ended', reason });
      }
    },
    // 038 (T025/FR-009) — a ≤2-min scoping reconnect: the transcript hydrates
    // silently; surface a brief "we got cut off" ack (no recap on this tier). A
    // >2-min gap instead runs the declinable welcome-back recap (server-driven).
    onReconnectNotice: () => {
      toast({ description: 'We got cut off — picking up where we left off.' });
    },
    // 015 — FR-VOICE-003: ending the voice session resets the lifecycle so the
    // next Start begins a fresh conversation (no reconnect to the ended one).
    onEndConversation: () => {
      lifecycle.reset();
    },
    // P1.3 — a hydrate 404 means the stored id points at a conversation that no
    // longer exists (expired / self-heal-swept / rolled back). Clear it so the
    // next boot starts fresh instead of reconnecting to a dead id.
    onOrphanedConversationId: () => {
      lifecycle.reset();
    },
    resumeConversationId,
    // Scoping is a full-transcript surface: enable silent transcript hydration
    // on resume so prior turns are loaded before the reconnect greeting arrives.
    // ANA-309 (FR-002d): gated on briefing-context resolving, so a stale stored id
    // is never hydrated before its resume target is confirmed.
    isDetailedConversationSurface: detailedSurfaceReady,
    // Expired scoping session: defer that hydration until the user re-enters via
    // the orient card (voice/text), so the returning offer shows first.
    resumeIsDeferred,
  });

  // Wrap sendMessage so the lifecycle state machine is notified on every user
  // send. This mints lifecycle.conversationId (null → UUID) on the first
  // message and keeps lastActivityAt current, enabling:
  //   • home → project navigation auto-reconnect (proactiveStart = true)
  //   • sessionStorage persistence for refresh-mid-conversation resumption
  const lifecycleSendMessage = lifecycle.sendMessage;
  const conversationSendMessage = conversation.sendMessage;

  const sendMessage = useCallback<ConversationReturn['sendMessage']>(
    async (msg, options) => {
      lifecycleSendMessage();
      return conversationSendMessage(msg, options);
    },
    [lifecycleSendMessage, conversationSendMessage],
  );

  // Keep ref in sync so the T013 effect always dispatches through the latest wrapper.
  sendMessageRef.current = sendMessage;

  const contextValue = useMemo(
    () => ({ ...conversation, sendMessage }),
    [conversation, sendMessage],
  );

  return (
    <ConversationContext.Provider value={contextValue}>
      <ConversationNavigationContext.Provider value={navigation}>
        {/* Stable mount for the agent audio sink so layout-mode toggles
            (peek ↔ detailed ↔ focused) never remount it and cut the call. */}
        <PersistentVoiceAudio room={conversation.room} livekitUrl={conversation.livekitUrl} />
        {children}
      </ConversationNavigationContext.Provider>
    </ConversationContext.Provider>
  );
}

export function ConversationProvider({ userId, orgId, children }: ProviderProps) {
  const lifecycle = useConversationLifecycle({ userId, orgId });

  return (
    <ConversationLifecycleContext.Provider value={lifecycle}>
      <ConversationInner orgId={orgId}>{children}</ConversationInner>
    </ConversationLifecycleContext.Provider>
  );
}
